import React, { FC, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import HotTable, { HotColumn } from '@handsontable/react';
import { IList } from 'core/API/listApi';
import { LinkageHeader, translateHeaders } from './helpers/HeadersList';
import Grid from '../Grid';
import { requireTitle } from './helpers/utils';
import { useTranslation } from 'helpers/languages/translations';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';
import ErrorModal from '../ErrorModal';

export type TPossibleErrors = 'duplicate_child' | 'same_parent' | 'duplicate_row';
export type TRowWithErrors = {
    'Master EAN': any;
    'Child EAN': any;
    Type: any;
    Errors?: string;
};

export const MLinkage: FC<{
    list: IList;
    toolId: number;
}> = ({ list, toolId }) => {
    const translatedHeaders = translateHeaders(LinkageHeader);
    const hotRef = useRef<React.ElementRef<typeof HotTable> | null>(null);
    const translate = useTranslation();
    const errorsRef = useRef<{ [key: number]: TPossibleErrors }>({});
    const typeErrorsRef = useRef<{ [key: number]: string }>({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [rowsToDownload, setRowsToDownload] = useState<TRowWithErrors[]>([]);
    const hasErrorsRef = useRef<boolean>(false);

    const translateExcelDataKeys = (data: TRowWithErrors[]) => {
        return data.map((row) => {
            const translatedRow: { [key: string]: any } = {};
            Object.keys(row).forEach((key, index) => {
                const translatedKey =
                    index === 3 ? translate('list.linkage.error_file.header.errors') : translatedHeaders[index];
                translatedRow[translatedKey as string] = row[key as keyof TRowWithErrors];
            });
            return translatedRow;
        });
    };

    const downloadFile = (data: any[], fileName: string) => {
        const worksheet = XLSX.utils.json_to_sheet(translateExcelDataKeys(data));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        FileSaver.saveAs(blob, `${fileName}.xlsx`);
    };

    const beforeValidate = (list: IList) => {
        const hot = hotRef?.current?.hotInstance;
        const data = (hot?.getData() || []).filter((row: any[]) =>
            row?.some((value: any) => value !== null && value !== ''),
        );
        let errorsFound = false;
        hasErrorsRef.current = false;

        const getTypedEanKey = (ean: string, type: string) => `${type}::${ean}`;

        const linkedList = new Map<string, { parentEAN: string; index: number }>();
        const parentOf = new Map<string, { parentEAN: string; index: number }>();
        const firstParentLineByEan = new Map<string, number>();
        const childToParentMap = new Map<string, { parentEAN: string; index: number }>();
        const rowsWithErrors: TRowWithErrors[] = [];

        const isChildDuplicate = (ean: string, type: string) => {
            return linkedList.has(getTypedEanKey(ean, type));
        };

        const checkForCycles = (ean: string, type: string): number | false => {
            const typedEan = getTypedEanKey(ean, type);
            let currentEan = typedEan;
            const visited = new Set<string>([typedEan]);
            let parent = parentOf.get(currentEan);
            while (parent) {
                if (parent.parentEAN === typedEan) return parent.index + 1; // Return the index of the row where the cycle is detected
                if (visited.has(parent.parentEAN)) return parent.index + 1;
                visited.add(parent.parentEAN);
                currentEan = parent.parentEAN;
                parent = parentOf.get(currentEan);
            }
            return false;
        };

        // to test before implementing

        // const checkForCylesV2 = (ean: string): number | false => {
        //     let currentEAN = ean;
        //     const visited = new Set<string>();
        //     while (true) {
        //         const parentEntry = parentOf.get(currentEAN);
        //         if (!parentEntry) break;
        //         if (visited.has(parentEntry.parentEAN)) {
        //             return parentEntry.index + 1; // Return the index of the row where the cycle is detected
        //         }
        //         visited.add(parentEntry.parentEAN);
        //         currentEAN = parentEntry.parentEAN;
        //     }
        //     return false;
        // }

        const missingParentTranslation = translate(`list.linkage.error.missing_field.parent_ean`) as string;
        const missingChildTranslation = translate(`list.linkage.error.missing_field.child_ean`) as string;
        const missingTypeTranslation = translate(`list.linkage.error.missing_field.type`) as string;
        const multipleParentsTranslation = translate(`list.linkage.error.multiple_parents`) as string;
        const cyclicLinkageTranslation = translate(`list.linkage.error.cyclic_linkage`) as string;
        const childIsParentTranslation = translate(`list.linkage.error.child_is_parent`) as string;

        for (let index = 0; index < data.length; index++) {
            const row = data[index];
            const errors: string[] = [];
            const parentEan = row[0];
            const childEan = row[1];
            const type = row[2];

            if (!row[0] || !row[1] || !row[2]) errorsFound = true;
            if (!parentEan) errors.push(missingParentTranslation);
            if (!childEan) errors.push(missingChildTranslation);
            if (!type) errors.push(missingTypeTranslation);
            if (parentEan === childEan) {
                errors.push(childIsParentTranslation);
                errorsFound = true;
            }

            const childFoundAsParentIndex = childEan ? firstParentLineByEan.get(childEan) : undefined;
            if (typeof childFoundAsParentIndex === 'number') {
                errors.push(
                    translate('list.linkage.error.child_is_already_parent', {
                        ean: childEan,
                        line: childFoundAsParentIndex + 2,
                    }) as string,
                );
                errorsFound = true;
            }

            if (parentEan && childEan && type) {
                const existingParent = childToParentMap.get(childEan);
                if (existingParent && existingParent.parentEAN !== parentEan) {
                    errors.push(
                        translate('list.linkage.error.child_has_multiple_parents', {
                            ean: childEan,
                            line: existingParent.index + 2,
                        }) as string,
                    );
                    errorsFound = true;
                } else if (isChildDuplicate(childEan, type)) {
                    errors.push(multipleParentsTranslation);
                    errorsFound = true;
                } else {
                    const typedChildEan = getTypedEanKey(childEan, type);
                    const typedParentEan = getTypedEanKey(parentEan, type);
                    linkedList.set(typedChildEan, { parentEAN: typedParentEan, index });
                    parentOf.set(typedChildEan, { parentEAN: typedParentEan, index });
                    if (!existingParent) {
                        childToParentMap.set(childEan, { parentEAN: parentEan, index });
                    }
                    const cyclicParentIndex = checkForCycles(childEan, type);
                    if (cyclicParentIndex) {
                        errors.push(cyclicLinkageTranslation);
                        errorsFound = true;
                    }
                }
            }

            if (parentEan && !firstParentLineByEan.has(parentEan)) {
                firstParentLineByEan.set(parentEan, index);
            }

            rowsWithErrors.push({
                'Master EAN': parentEan,
                'Child EAN': childEan,
                Type: type,
                Errors: errors.join('; '),
            });
        }

        errorsRef.current = {};
        typeErrorsRef.current = {};

        if (errorsFound) {
            hasErrorsRef.current = true;
            setRowsToDownload(rowsWithErrors);
            setIsModalOpen(true);
        }
    };

    const afterPaste = async (data: any, coords: any) => {
        const hot = hotRef?.current?.hotInstance;
        hot?.validateCells();
    };

    return (
        <Box m={4}>
            <Grid
                paramHotRef={hotRef}
                toolId={toolId}
                headers={LinkageHeader}
                list={list}
                beforeValidate={beforeValidate}
                afterPaste={afterPaste}
                hasErrors={hasErrorsRef}
            >
                <HotColumn title={requireTitle(translatedHeaders[0])} />
                <HotColumn title={requireTitle(translatedHeaders[1])} />
                <HotColumn title={requireTitle(translatedHeaders[2])} type="text" />
            </Grid>
            {
                <ErrorModal
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false);
                        setRowsToDownload([]);
                    }}
                    onSubmit={() => {
                        downloadFile(rowsToDownload, 'linkage_list_Errors');
                    }}
                    isLoading={false}
                />
            }
        </Box>
    );
};
